import { pascalCase } from 'module/helpers/pascal-case';
import { unreachable } from 'module/helpers/unreachable';
import { DateField } from 'module/types/filter-field/field/filterable-field';
import { kebabCase } from 'lodash';
import moment, { Moment } from 'moment';
import { TermSeparator } from '../separators';

export enum DateFieldFilterOperationType {
  IsBeforeToday = 'IsBeforeToday',
  IsToday = 'IsToday',
  IsTomorrow = 'IsTomorrow',
  IsThisWeek = 'IsThisWeek',
  IsLastWeek = 'IsLastWeek',
  IsNextWeek = 'IsNextWeek',
  IsAny = 'IsAny',
  IsThisMonth = 'IsThisMonth',
  IsLastMonth = 'IsLastMonth',
  IsThisQuarter = 'IsThisQuarter',
  IsLastQuarter = 'IsLastQuarter',
  IsThisYear = 'IsThisYear',

  // Relative
  IsMoreThan = 'IsMoreThan',
  IsExactly = 'IsExactly',
  IsLessThan = 'IsLessThan',

  // Absolute
  IsAfter = 'IsAfter',
  IsOn = 'IsOn',
  IsBefore = 'IsBefore',

  // Optional
  IsEmpty = 'IsEmpty',
  IsNotEmpty = 'IsNotEmpty',
}

export const DateFieldFilterDefaultOperationTypes = [
  DateFieldFilterOperationType.IsBeforeToday,
  DateFieldFilterOperationType.IsToday,
  DateFieldFilterOperationType.IsTomorrow,
  DateFieldFilterOperationType.IsThisWeek,
  DateFieldFilterOperationType.IsLastWeek,
  DateFieldFilterOperationType.IsNextWeek,
  DateFieldFilterOperationType.IsThisMonth,
  DateFieldFilterOperationType.IsLastMonth,
  DateFieldFilterOperationType.IsThisYear,
  DateFieldFilterOperationType.IsThisQuarter,
  DateFieldFilterOperationType.IsLastQuarter,
  DateFieldFilterOperationType.IsAny,
];

export const DateFieldFilterRelativeOperationTypes = [
  DateFieldFilterOperationType.IsMoreThan,
  DateFieldFilterOperationType.IsExactly,
  DateFieldFilterOperationType.IsLessThan,
];

export const DateFieldFilterAbsoluteOperationTypes = [
  DateFieldFilterOperationType.IsAfter,
  DateFieldFilterOperationType.IsOn,
  DateFieldFilterOperationType.IsBefore,
];

export const DateFieldFilterOptionalOperationTypes = [
  DateFieldFilterOperationType.IsEmpty,
  DateFieldFilterOperationType.IsNotEmpty,
];

export const DateFieldFilterOperationTypes = [
  ...DateFieldFilterDefaultOperationTypes,
  ...DateFieldFilterRelativeOperationTypes,
  ...DateFieldFilterAbsoluteOperationTypes,
  ...DateFieldFilterOptionalOperationTypes,
];

export const isOptional = (type: DateFieldFilterOperationType): boolean =>
  DateFieldFilterOptionalOperationTypes.includes(type);

export const isDefault = (type: DateFieldFilterOperationType): boolean =>
  type === DateFieldFilterOperationType.IsBeforeToday ||
  type === DateFieldFilterOperationType.IsToday ||
  type === DateFieldFilterOperationType.IsTomorrow ||
  type === DateFieldFilterOperationType.IsThisWeek ||
  type === DateFieldFilterOperationType.IsNextWeek ||
  type === DateFieldFilterOperationType.IsThisMonth ||
  type === DateFieldFilterOperationType.IsAny ||
  type === DateFieldFilterOperationType.IsMoreThan ||
  type === DateFieldFilterOperationType.IsExactly ||
  type === DateFieldFilterOperationType.IsLessThan ||
  type === DateFieldFilterOperationType.IsAfter ||
  type === DateFieldFilterOperationType.IsOn ||
  type === DateFieldFilterOperationType.IsBefore ||
  type === DateFieldFilterOperationType.IsEmpty ||
  type === DateFieldFilterOperationType.IsNotEmpty;

interface DateFieldFilterBase {
  field: DateField;
}

export enum RelativeDirection {
  Ago = 'Ago',
  FromNow = 'FromNow',
}

export enum ApiRelativeDirection {
  Ago = 'ago',
  After = 'after',
}

interface DateFieldAbsoluteFilterBase extends DateFieldFilterBase {
  value?: Moment;
}

interface DateFieldRelativeFilterBase extends DateFieldFilterBase {
  direction: RelativeDirection;
  days?: number;
}

interface DateFieldIsBeforeTodayFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsBeforeToday;
}

interface DateFieldIsTodayFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsToday;
}

interface DateFieldIsTomorrowFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsTomorrow;
}

interface DateFieldIsThisWeekFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsThisWeek;
}

interface DateFieldIsLastWeekFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsLastWeek;
}

interface DateFieldIsNextWeekFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsNextWeek;
}

interface DateFieldIsThisMonthFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsThisMonth;
}
interface DateFieldIsLastMonthFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsLastMonth;
}
interface DateFieldIsThisQuarterFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsThisQuarter;
}
interface DateFieldIsLastQuarterFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsLastQuarter;
}

interface DateFieldIsLastYearFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsThisYear;
}

interface DateFieldIsAnyFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsAny;
}

interface DateFieldIsMoreThanFilter extends DateFieldRelativeFilterBase {
  type: DateFieldFilterOperationType.IsMoreThan;
}

interface DateFieldIsExactlyFilter extends DateFieldRelativeFilterBase {
  type: DateFieldFilterOperationType.IsExactly;
}

interface DateFieldIsLessThanFilter extends DateFieldRelativeFilterBase {
  type: DateFieldFilterOperationType.IsLessThan;
}

export type DateFieldRelativeFilter =
  | DateFieldIsMoreThanFilter
  | DateFieldIsExactlyFilter
  | DateFieldIsLessThanFilter;

interface DateFieldIsAfterFilter extends DateFieldAbsoluteFilterBase {
  type: DateFieldFilterOperationType.IsAfter;
}

interface DateFieldIsOnFilter extends DateFieldAbsoluteFilterBase {
  type: DateFieldFilterOperationType.IsOn;
}

interface DateFieldIsBeforeFilter extends DateFieldAbsoluteFilterBase {
  type: DateFieldFilterOperationType.IsBefore;
}

export type DateFieldFilterRelativeOperationType =
  | DateFieldFilterOperationType.IsMoreThan
  | DateFieldFilterOperationType.IsExactly
  | DateFieldFilterOperationType.IsLessThan;

export type DateFieldFilterAbsoluteOperationType =
  | DateFieldFilterOperationType.IsAfter
  | DateFieldFilterOperationType.IsOn
  | DateFieldFilterOperationType.IsBefore;

export type DateFieldAbsoluteFilter =
  | DateFieldIsAfterFilter
  | DateFieldIsOnFilter
  | DateFieldIsBeforeFilter;

interface DateFieldIsEmptyFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsEmpty;
}

interface DateFieldIsNotEmptyFilter extends DateFieldFilterBase {
  type: DateFieldFilterOperationType.IsNotEmpty;
}

export type DateFieldFilter =
  | DateFieldIsBeforeTodayFilter
  | DateFieldIsTodayFilter
  | DateFieldIsTomorrowFilter
  | DateFieldIsThisWeekFilter
  | DateFieldIsLastWeekFilter
  | DateFieldIsNextWeekFilter
  | DateFieldIsThisMonthFilter
  | DateFieldIsLastMonthFilter
  | DateFieldIsThisQuarterFilter
  | DateFieldIsLastQuarterFilter
  | DateFieldIsLastYearFilter
  | DateFieldIsAnyFilter
  | DateFieldRelativeFilter
  | DateFieldAbsoluteFilter
  | DateFieldIsEmptyFilter
  | DateFieldIsNotEmptyFilter;

const isValid = (filter: DateFieldFilter) => {
  switch (filter.type) {
    case DateFieldFilterOperationType.IsAny:
      return false;
    case DateFieldFilterOperationType.IsMoreThan:
    case DateFieldFilterOperationType.IsExactly:
    case DateFieldFilterOperationType.IsLessThan:
      return filter.days != null;
    case DateFieldFilterOperationType.IsAfter:
    case DateFieldFilterOperationType.IsOn:
    case DateFieldFilterOperationType.IsBefore:
      return filter.value != null;
    default:
      return true;
  }
};

const toString = (filter: DateFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case DateFieldFilterOperationType.IsEmpty:
    case DateFieldFilterOperationType.IsNotEmpty:
    case DateFieldFilterOperationType.IsBeforeToday:
    case DateFieldFilterOperationType.IsToday:
    case DateFieldFilterOperationType.IsTomorrow:
    case DateFieldFilterOperationType.IsThisWeek:
    case DateFieldFilterOperationType.IsLastWeek:
    case DateFieldFilterOperationType.IsNextWeek:
    case DateFieldFilterOperationType.IsThisMonth:
    case DateFieldFilterOperationType.IsLastMonth:
    case DateFieldFilterOperationType.IsThisQuarter:
    case DateFieldFilterOperationType.IsLastQuarter:
    case DateFieldFilterOperationType.IsThisYear:
    case DateFieldFilterOperationType.IsAny:
      return type;
    case DateFieldFilterOperationType.IsMoreThan:
    case DateFieldFilterOperationType.IsExactly:
    case DateFieldFilterOperationType.IsLessThan:
      return [
        type,
        `${JSON.stringify(filter.days)}~${kebabCase(filter.direction)}`,
      ].join(TermSeparator);
    case DateFieldFilterOperationType.IsAfter:
    case DateFieldFilterOperationType.IsOn:
    case DateFieldFilterOperationType.IsBefore:
      return [
        type,
        filter.value == null ? 'null' : filter.value.format('YYYY-MM-DD'),
      ].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: DateField): DateFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as DateFieldFilterOperationType;

  switch (type) {
    case DateFieldFilterOperationType.IsEmpty:
    case DateFieldFilterOperationType.IsNotEmpty:
    case DateFieldFilterOperationType.IsBeforeToday:
    case DateFieldFilterOperationType.IsToday:
    case DateFieldFilterOperationType.IsTomorrow:
    case DateFieldFilterOperationType.IsThisWeek:
    case DateFieldFilterOperationType.IsLastWeek:
    case DateFieldFilterOperationType.IsNextWeek:
    case DateFieldFilterOperationType.IsThisMonth:
    case DateFieldFilterOperationType.IsLastMonth:
    case DateFieldFilterOperationType.IsThisQuarter:
    case DateFieldFilterOperationType.IsLastQuarter:
    case DateFieldFilterOperationType.IsThisYear:
    case DateFieldFilterOperationType.IsAny:
      return { field, type };
    case DateFieldFilterOperationType.IsMoreThan:
    case DateFieldFilterOperationType.IsExactly:
    case DateFieldFilterOperationType.IsLessThan: {
      const [days, direction] = meta.split('~');

      return {
        field,
        type,
        days: JSON.parse(days),
        direction: pascalCase(direction) as RelativeDirection,
      };
    }
    case DateFieldFilterOperationType.IsAfter:
    case DateFieldFilterOperationType.IsOn:
    case DateFieldFilterOperationType.IsBefore: {
      const moment_value = JSON.parse(`"${meta}"`) as string | null;

      return {
        field,
        type,
        value: moment_value == null ? undefined : moment(moment_value),
      };
    }
    default:
      return unreachable(type);
  }
};

export const DateFieldFilter = {
  isValid,
  toString,
  parse,
};

export const isRelativeFilter = (
  arg: DateFieldFilter,
): arg is DateFieldRelativeFilter => {
  return DateFieldFilterRelativeOperationTypes.includes(arg as any);
};

export const isAbsoluteFilter = (
  arg: DateFieldFilter,
): arg is DateFieldAbsoluteFilter => {
  return DateFieldFilterAbsoluteOperationTypes.includes(arg as any);
};

const availabletriggerFilters = [
  ...DateFieldFilterOptionalOperationTypes,
  ...DateFieldFilterAbsoluteOperationTypes,
];
const validDateTriggerFilters = (type: DateFieldFilterOperationType): boolean =>
  availabletriggerFilters.includes(type);

export const dateFilterOperationOptions = (field: DateField) =>
  DateFieldFilterOperationTypes.filter((x) => validDateTriggerFilters(x)).map(
    (type) =>
      ({
        field,
        type,
      }) as any as DateFieldFilter,
  );
